import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  MenuItem,
  Button,
  Paper,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  Description as DescriptionIcon,
  Business as BusinessIcon,
  ExitToApp as ExitToAppIcon,
  ContactMail as ContactMailIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  AutoAwesome as AutoAwesomeIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import ThemeToggle from "./components/ThemeToggle";
import LanguageToggle from "./components/LanguageToggle";
import SEOHead from "./components/SEOHead";
import {
  DocumentType,
  TemplateType,
  ToneType,
  DocumentFields,
  DocumentDraft,
  EMPTY_FIELDS,
  REQUIRED_FIELDS,
} from "./types/document";
import "./index.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

const DOC_TYPES: { type: DocumentType; icon: React.ReactNode }[] = [
  { type: "coverLetter", icon: <DescriptionIcon sx={{ fontSize: 32 }} /> },
  { type: "formalBusiness", icon: <BusinessIcon sx={{ fontSize: 32 }} /> },
  { type: "resignation", icon: <ExitToAppIcon sx={{ fontSize: 32 }} /> },
  { type: "referenceRequest", icon: <ContactMailIcon sx={{ fontSize: 32 }} /> },
];

const TEMPLATES: TemplateType[] = ["classic", "modern", "corporate"];
const TONES: ToneType[] = ["professional", "formal", "friendly", "concise"];

// Suggestions shown as quick-tap chips under a field
const FIELD_SUGGESTIONS: Partial<Record<keyof DocumentFields, string[]>> = {
  recipientName: ["Hiring Manager", "HR Manager", "HR Team"],
  recipientTitle: ["HR Manager", "Hiring Manager", "Recruitment Manager", "Department Manager", "Director"],
  noticePeriod: ["2 weeks", "1 month", "30 days", "Immediate"],
  referenceRelationship: ["Former Manager", "Direct Manager", "Colleague", "Professor", "Client"],
  purposeOfReference: ["Job application", "Graduate school application", "Visa application"],
  letterPurpose: ["Request a meeting", "Follow up on a proposal", "Raise a complaint", "Partnership inquiry"],
  tone: [],
};

type FieldConfig = {
  key: keyof DocumentFields;
  multiline?: boolean;
  rows?: number;
  optional?: boolean;
};

const FIELD_GROUPS: Record<DocumentType, FieldConfig[][]> = {
  coverLetter: [
    [
      { key: "yourName" },
      { key: "yourEmail" },
      { key: "yourPhone", optional: true },
    ],
    [
      { key: "recipientName" },
      { key: "companyName" },
      { key: "jobTitle" },
    ],
    [
      { key: "yourBackground", multiline: true, rows: 3 },
      { key: "whyThisRole", multiline: true, rows: 3 },
      { key: "tone" },
    ],
  ],
  formalBusiness: [
    [
      { key: "yourName" },
      { key: "yourEmail" },
      { key: "yourPhone", optional: true },
    ],
    [
      { key: "recipientName" },
      { key: "companyName" },
    ],
    [
      { key: "letterPurpose" },
      { key: "keyMessage", multiline: true, rows: 4 },
      { key: "tone" },
    ],
  ],
  resignation: [
    [
      { key: "yourName" },
      { key: "yourEmail" },
    ],
    [
      { key: "recipientName" },
      { key: "companyName" },
    ],
    [
      { key: "noticePeriod" },
      { key: "lastWorkingDay" },
      { key: "reasonForLeaving", multiline: true, rows: 2, optional: true },
      { key: "tone" },
    ],
  ],
  referenceRequest: [
    [
      { key: "yourName" },
      { key: "yourEmail" },
    ],
    [
      { key: "referenceName" },
      { key: "referenceRelationship" },
      { key: "purposeOfReference" },
    ],
    [
      { key: "keyMessage", multiline: true, rows: 3, optional: true },
      { key: "tone" },
    ],
  ],
};

const LS_KEY = "docwriter_fields";

const loadSavedFields = (): Partial<DocumentFields> => {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}"); } catch { return {}; }
};

const saveFields = (f: DocumentFields) => {
  // Save everything except the date (auto-set) and tone (has a sensible default)
  const { date, ...rest } = f;
  localStorage.setItem(LS_KEY, JSON.stringify(rest));
};

// ── Main App Inner ────────────────────────────────────────────────────────────
const AppInner: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { t, isRTL } = useLanguage();

  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [docType, setDocType] = useState<DocumentType | null>(null);
  const [template, setTemplate] = useState<TemplateType>("modern");
  const [fields, setFields] = useState<DocumentFields>(() => ({
    ...EMPTY_FIELDS,
    ...loadSavedFields(),
  }));
  const [editedDraft, setEditedDraft] = useState<DocumentDraft | null>(null);
  const [drafting, setDrafting] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [error, setError] = useState("");
  const [hasSavedData, setHasSavedData] = useState(() => {
    const saved = loadSavedFields();
    return Object.values(saved).some((v) => typeof v === "string" && v.trim() !== "" && v !== "professional");
  });

  const orange = "#FF8428";

  const handleSelectType = (type: DocumentType) => {
    setDocType(type);
    setEditedDraft(null);
    setError("");
  };

  const handleFieldChange = (key: keyof DocumentFields, value: string) => {
    setFields((prev) => {
      const updated = { ...prev, [key]: value };
      saveFields(updated);
      setHasSavedData(!!(updated.yourName || updated.yourEmail || updated.yourBackground || updated.keyMessage));
      return updated;
    });
  };

  const handleClearSaved = () => {
    localStorage.removeItem(LS_KEY);
    setFields({ ...EMPTY_FIELDS });
    setHasSavedData(false);
  };

  const handleDraft = async () => {
    setDrafting(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/draft-document`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ docType, fields }),
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const data = await res.json();
      setEditedDraft(data);
    } catch (err: any) {
      setError(`${t("errors.failedToDraft")}: ${err.message}`);
    } finally {
      setDrafting(false);
    }
  };

  const handleNext = async () => {
    if (step === 0 && !docType) return;
    if (step === 1) {
      const missing = REQUIRED_FIELDS[docType!].some((k) => !fields[k]?.trim());
      if (missing) { setError(t("errors.fillRequired")); return; }
      setError("");
      setStep(2);
      await handleDraft();
      return;
    }
    setStep((s) => Math.min(s + 1, 2) as 0 | 1 | 2);
  };

  const handleBack = () => { setError(""); setStep((s) => Math.max(s - 1, 0) as 0 | 1 | 2); };

  const handleStartOver = () => {
    setStep(0); setDocType(null); setFields({ ...EMPTY_FIELDS });
    setEditedDraft(null); setError("");
  };

  const handleGeneratePdf = async () => {
    if (!editedDraft || !docType) return;
    setGeneratingPdf(true); setError("");
    try {
      const res = await fetch(`${API_URL}/generate-document-pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ docType, fields, draft: editedDraft, template }),
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${docType}-${fields.yourName.replace(/\s+/g, "-") || "document"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(`${t("errors.failedToGeneratePDF")}: ${err.message}`);
    } finally {
      setGeneratingPdf(false);
    }
  };

  // ── Step 0: Choose type ───────────────────────────────────────────────────
  const renderStep0 = () => (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, letterSpacing: "-0.02em" }}>
        {t("steps.chooseType")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        {t("app.subtitle")}
      </Typography>

      <Grid container spacing={2} columns={12}>
        {DOC_TYPES.map(({ type, icon }) => (
          <Grid key={type} size={{ xs: 12, sm: 6 }}>
            <Paper
              onClick={() => handleSelectType(type)}
              sx={{
                p: 3, borderRadius: 3, cursor: "pointer",
                border: "2px solid",
                borderColor: docType === type ? orange : "transparent",
                backgroundColor: docType === type
                  ? isDark ? "rgba(255,132,40,0.14)" : "rgba(255,132,40,0.08)"
                  : isDark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.45)",
                backdropFilter: "blur(12px)",
                transition: "all 0.15s ease",
                "&:hover": {
                  borderColor: orange, transform: "translateY(-2px)",
                  boxShadow: isDark ? "0 6px 20px rgba(0,0,0,0.4)" : "0 6px 20px rgba(79,48,29,0.1)",
                },
              }}
            >
              <Box sx={{ color: orange, mb: 1.5 }}>{icon}</Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                {t(`documentTypes.${type}`)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(`documentTypes.${type}Desc`)}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Template picker */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: "text.secondary", textTransform: isRTL ? "none" : "uppercase", fontSize: "0.72rem", letterSpacing: isRTL ? "normal" : "0.08em" }}>
          {t("labels.template")}
        </Typography>
        <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap", gap: 1.5 }}>
          {TEMPLATES.map((tmpl) => (
            <Paper
              key={tmpl}
              onClick={() => setTemplate(tmpl)}
              sx={{
                px: 2.5, py: 1.5, borderRadius: 2, cursor: "pointer",
                border: "2px solid",
                borderColor: template === tmpl ? orange : "divider",
                backgroundColor: template === tmpl
                  ? isDark ? "rgba(255,132,40,0.14)" : "rgba(255,132,40,0.08)"
                  : isDark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.40)",
                backdropFilter: "blur(12px)",
                transition: "all 0.15s", minWidth: 110,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700, color: template === tmpl ? orange : "text.primary" }}>
                {t(`templates.${tmpl}`)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {t(`templates.${tmpl}Desc`)}
              </Typography>
            </Paper>
          ))}
        </Stack>
      </Box>
    </Box>
  );

  // ── Step 1: Fields ────────────────────────────────────────────────────────
  const renderStep1 = () => {
    if (!docType) return null;
    const groups = FIELD_GROUPS[docType];

    const fieldSx = {
      "& .MuiOutlinedInput-root": {
        borderRadius: 2,
        backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.7)",
        backdropFilter: "blur(8px)",
        transition: "background 0.15s",
        "&:hover": {
          backgroundColor: isDark ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.9)",
        },
        "&.Mui-focused": {
          backgroundColor: isDark ? "rgba(255,255,255,0.09)" : "rgba(255,255,255,1)",
        },
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)",
      },
    };

    return (
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5, letterSpacing: "-0.02em" }}>
          {t(`documentTypes.${docType}`)}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: hasSavedData ? 2 : 4 }}>
          {t("labels.previewNotice")}
        </Typography>

        {hasSavedData && (
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            mb: 3, px: 2, py: 1.2, borderRadius: 2,
            backgroundColor: isDark ? "rgba(255,132,40,0.08)" : "rgba(255,132,40,0.07)",
            border: "1px solid", borderColor: isDark ? "rgba(255,132,40,0.2)" : "rgba(255,132,40,0.18)",
          }}>
            <Typography variant="caption" sx={{ color: orange, fontWeight: 600 }}>
              ✓ Your info was remembered
            </Typography>
            <Button size="small" onClick={handleClearSaved} sx={{ color: "text.secondary", fontSize: "0.72rem", minWidth: 0, p: "2px 8px", textTransform: "none" }}>
              Clear saved data
            </Button>
          </Box>
        )}
        <Stack spacing={4}>
          {groups.map((group, gi) => (
            <Box key={gi}>
              <Stack spacing={2.5}>
                {group.map(({ key, multiline, rows, optional }) => {
                  const suggestions = FIELD_SUGGESTIONS[key] || [];
                  const isRequired = REQUIRED_FIELDS[docType].includes(key) && !optional;

                  if (key === "tone") {
                    return (
                      <TextField
                        key={key} select
                        label={t(`fields.${key}`)}
                        value={fields[key]}
                        onChange={(e) => handleFieldChange(key, e.target.value)}
                        size="small" fullWidth sx={fieldSx}
                      >
                        {TONES.map((tone) => (
                          <MenuItem key={tone} value={tone}>{t(`toneOptions.${tone}`)}</MenuItem>
                        ))}
                      </TextField>
                    );
                  }

                  return (
                    <Box key={key}>
                      <TextField
                        label={t(`fields.${key}`)}
                        value={fields[key]}
                        onChange={(e) => handleFieldChange(key, e.target.value)}
                        multiline={multiline} rows={rows}
                        size="small" fullWidth
                        required={isRequired}
                        placeholder={optional ? "Optional" : undefined}
                        sx={fieldSx}
                      />
                      {suggestions.length > 0 && (
                        <Stack direction="row" sx={{ mt: 0.75, flexWrap: "wrap", gap: 0.75 }}>
                          {suggestions.map((s) => (
                            <Chip
                              key={s}
                              label={s}
                              size="small"
                              onClick={() => handleFieldChange(key, s)}
                              sx={{
                                fontSize: "0.72rem",
                                height: 24,
                                cursor: "pointer",
                                backgroundColor: fields[key] === s
                                  ? isDark ? "rgba(255,132,40,0.25)" : "rgba(255,132,40,0.15)"
                                  : isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)",
                                color: fields[key] === s ? orange : "text.secondary",
                                border: "1px solid",
                                borderColor: fields[key] === s
                                  ? isDark ? "rgba(255,132,40,0.4)" : "rgba(255,132,40,0.3)"
                                  : "transparent",
                                "&:hover": {
                                  backgroundColor: isDark ? "rgba(255,132,40,0.2)" : "rgba(255,132,40,0.12)",
                                  color: orange,
                                },
                              }}
                            />
                          ))}
                        </Stack>
                      )}
                    </Box>
                  );
                })}
              </Stack>
              {gi < groups.length - 1 && <Divider sx={{ mt: 3 }} />}
            </Box>
          ))}
        </Stack>
      </Box>
    );
  };

  // ── Step 2: Review & download ─────────────────────────────────────────────
  const renderStep2 = () => (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 2, letterSpacing: "-0.02em" }}>
        {t("steps.reviewEdit")}
      </Typography>

      {drafting ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, gap: 2 }}>
          <CircularProgress sx={{ color: orange }} />
          <Typography variant="body2" color="text.secondary">{t("buttons.drafting")}</Typography>
        </Box>
      ) : editedDraft ? (
        <Box>
          <Alert severity="success" sx={{
            mb: 3, borderRadius: 2,
            backgroundColor: isDark ? "rgba(255,180,106,0.1)" : "rgba(255,132,40,0.08)",
            color: "text.primary",
            "& .MuiAlert-icon": { color: orange },
            border: "1px solid",
            borderColor: isDark ? "rgba(255,180,106,0.2)" : "rgba(255,132,40,0.2)",
          }}>
            {t("labels.draftReady")}
          </Alert>

          <Stack spacing={2.5}>
            <TextField
              label="Subject / Re:"
              value={editedDraft.subject}
              onChange={(e) => setEditedDraft((d) => d ? { ...d, subject: e.target.value } : d)}
              size="small" fullWidth
            />
            <TextField
              label="Letter Body"
              value={editedDraft.body}
              onChange={(e) => setEditedDraft((d) => d ? { ...d, body: e.target.value } : d)}
              multiline rows={16} fullWidth
              sx={{ "& .MuiInputBase-root": { fontFamily: "Georgia, serif", fontSize: "0.95rem", lineHeight: 1.8 } }}
            />
          </Stack>

          <Stack direction="row" useFlexGap spacing={2} sx={{ mt: 3, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              startIcon={generatingPdf ? <CircularProgress size={16} sx={{ color: "white" }} /> : <DownloadIcon />}
              onClick={handleGeneratePdf}
              disabled={generatingPdf}
              sx={{ backgroundColor: orange, "&:hover": { backgroundColor: "#e0721e" }, fontWeight: 700, borderRadius: 2, px: 3 }}
            >
              {generatingPdf ? t("buttons.generating") : t("buttons.downloadPDF")}
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleDraft}
              disabled={drafting || generatingPdf}
              sx={{ borderRadius: 2, borderColor: orange, color: orange }}
            >
              Re-draft
            </Button>
          </Stack>
        </Box>
      ) : null}
    </Box>
  );

  // ── Layout ────────────────────────────────────────────────────────────────
  return (
    <>
      <SEOHead
        title="AI Document Writer — Free Cover Letter Generator | my-aitools"
        description="Write professional cover letters, resignation letters, formal business letters and more in seconds. AI drafts, you edit, download as PDF. Free, no sign-up."
        keywords="cover letter generator, AI document writer, formal letter template, resignation letter UAE, professional letter writer free"
        canonicalUrl="https://my-aitools.online/document-writer/"
      />
      {/* Top-right controls: home brand + language + theme — grouped for alignment */}
      <Box sx={{
        position: "fixed", top: 16, right: 16,
        zIndex: 1200, display: "flex", alignItems: "center", gap: 1,
        direction: "ltr",
      }}>
        <Box
          component="a"
          href="/"
          onContextMenu={(e) => e.preventDefault()}
          sx={{
            display: "flex", alignItems: "center", gap: 0.75, mr: 0.5,
            textDecoration: "none", color: "text.secondary",
            "&:hover": { color: "text.primary" },
            transition: "color 0.2s ease",
          }}
        >
          <Box sx={{
            width: 24, height: 24, borderRadius: "7px", flexShrink: 0,
            background: `url(${process.env.PUBLIC_URL}/icon.svg) center/contain no-repeat`,
            userSelect: "none",
          }} />
          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.8rem", userSelect: "none" }}>
            Home
          </Typography>
        </Box>
        <LanguageToggle />
        <ThemeToggle />
      </Box>

      <Box className="ambient-stage" aria-hidden="true">
        <Box className="orb orb-one" />
        <Box className="orb orb-two" />
        <Box className="orb orb-three" />
      </Box>

      <Box sx={{ minHeight: "100vh", position: "relative" }}>

        {/* Hero panel */}
        <Container maxWidth="lg" sx={{ pt: { xs: "5.75rem", md: "5rem" }, pb: 0 }}>
          <Paper className="hero-panel" elevation={0}>
            <Grid container spacing={4} sx={{ alignItems: "center" }}>
              <Grid size={{ xs: 12, md: 7 }}>
                <Typography variant="h2" component="h1" className="hero-title">
                  {t("app.title")}
                </Typography>
                <Typography variant="h6" className="hero-subtitle">
                  {t("app.subtitle")}
                </Typography>
                <Stack className="hero-feature-chips" direction="row" sx={{ mt: 3, flexWrap: "wrap", gap: 1.5 }}>
                  <Chip className="hero-feature-chip" icon={<DescriptionIcon />} label="4 document types" />
                  <Chip className="hero-feature-chip" icon={<AutoAwesomeIcon />} label="AI-drafted instantly" />
                  <Chip className="hero-feature-chip" icon={<DownloadIcon />} label="PDF export" />
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <Box className="hero-simple-card">
                  <Typography
                    variant="overline"
                    sx={isRTL ? { letterSpacing: "normal", textTransform: "none" } : undefined}
                  >
                    {t("labels.howItWorks")}
                  </Typography>
                  <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                    {[
                      t("labels.heroStep1"),
                      t("labels.heroStep2"),
                      t("labels.heroStep3"),
                    ].map((step, i) => (
                      <Stack key={i} className="hero-step-row" direction="row" sx={{ alignItems: "flex-start" }}>
                        <Box className="step-number">{i + 1}</Box>
                        <Typography className="hero-step-text" variant="body2">{step}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Container>

        {/* Content */}
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 5 } }}>
          <Stepper activeStep={step} sx={{
            mb: 5,
            "& .MuiStepLabel-label": { fontSize: "0.8rem", fontWeight: 600 },
            "& .MuiStepIcon-root.Mui-active": { color: orange },
            "& .MuiStepIcon-root.Mui-completed": { color: orange },
          }}>
            {[t("steps.chooseType"), t("steps.fillDetails"), t("steps.reviewEdit")].map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>

          <Paper sx={{
            p: { xs: 3, md: 5 }, borderRadius: 4,
            backgroundColor: isDark ? "rgba(15,9,6,0.30)" : "rgba(255,252,248,0.62)",
            backdropFilter: "blur(48px)",
            border: "1px solid",
            borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.75)",
            boxShadow: isDark ? "0 8px 40px rgba(0,0,0,0.35)" : "0 8px 40px rgba(120,60,20,0.08)",
          }}>
            {step === 0 && renderStep0()}
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}

            {error && (
              <Alert severity="error" sx={{
                mt: 3, borderRadius: 3,
                backgroundColor: "rgba(200,50,50,0.08)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(200,50,50,0.2)",
                color: "text.primary",
                "& .MuiAlert-icon": { color: "#e05555" },
                "& .MuiAlert-message": { fontWeight: 500 },
              }}>{error}</Alert>
            )}

            {/* Nav */}
            <Stack direction="row" sx={{ mt: 4, justifyContent: "space-between" }}>
              {step > 0 ? (
                <Button
                  startIcon={isRTL ? <ArrowForwardIcon /> : <ArrowBackIcon />}
                  onClick={step === 2 ? handleStartOver : handleBack}
                  sx={{ color: "text.secondary", fontWeight: 600 }}
                >
                  {step === 2 ? t("buttons.startOver") : t("buttons.back")}
                </Button>
              ) : <Box />}

              {step < 2 && (
                <Button
                  variant="contained"
                  endIcon={step === 1
                    ? drafting ? <CircularProgress size={16} sx={{ color: "white" }} /> : <AutoAwesomeIcon />
                    : isRTL ? <ArrowBackIcon /> : <ArrowForwardIcon />}
                  onClick={handleNext}
                  disabled={step === 0 ? !docType : drafting}
                  sx={{ backgroundColor: orange, "&:hover": { backgroundColor: "#e0721e" }, fontWeight: 700, borderRadius: 2, px: 3 }}
                >
                  {step === 1
                    ? drafting ? t("buttons.drafting") : t("buttons.draftWithAI")
                    : t("buttons.next")}
                </Button>
              )}
            </Stack>
          </Paper>
        </Container>

        {/* Footer */}
        <Box component="footer" sx={{ borderTop: "1px solid", borderColor: "divider", py: 3, textAlign: "center" }}>
          <Typography variant="caption" color="text.secondary">
            <a href="https://my-aitools.online/" style={{ color: "inherit" }}>my-aitools.online</a>
            {" · "}
            <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer" style={{ color: "inherit" }}>Privacy Policy</a>
          </Typography>
        </Box>
      </Box>
    </>
  );
};

const App: React.FC = () => (
  <ThemeProvider>
    <LanguageProvider>
      <AppInner />
    </LanguageProvider>
  </ThemeProvider>
);

export default App;
